import {
  AssignmentTurnedIn,
  CalendarArrowDown,
  CalendarArrowUp,
  Event,
  FilePen,
  FilePlus,
  Globe,
  Phone,
  Pin,
  Video,
} from "@gigpro/ui/Icons";
import type { Filters } from "domains/schedule/components/Filters";
import type { MarketplaceSearch } from "domains/schedule/utils/useMarketplaceSearch";

export const sortOptions = [
  { id: "start_time", name: "Soonest First", icon: CalendarArrowUp },
  { id: "-start_time", name: "Latest First", icon: CalendarArrowDown },
  { id: "-created_ts", name: "Recently Posted", icon: FilePlus },
  { id: "-last_updated_ts", name: "Recently Edited", icon: FilePen },
] as const satisfies NonNullable<React.ComponentProps<typeof Filters>["sort"]>["options"];

export const historySortOptions = [
  { id: "-start_time", name: "Newest First", icon: CalendarArrowDown },
  { id: "start_time", name: "Oldest First", icon: CalendarArrowUp },
] as const satisfies NonNullable<React.ComponentProps<typeof Filters>["sort"]>["options"];

export const paySortOptions = [
  { id: "end_time", name: "Oldest First", icon: CalendarArrowUp },
  { id: "-end_time", name: "Newest First", icon: CalendarArrowDown },
] as const satisfies NonNullable<React.ComponentProps<typeof Filters>["sort"]>["options"];

export const gigStatusOptions = [
  {
    id: "unfilled",
    name: "Unfilled",
    controlClassName: "data-[state=checked]:border-gray data-[state=checked]:bg-gray",
  },
  {
    id: "ready",
    name: "Filled",
    controlClassName: "data-[state=checked]:border-info data-[state=checked]:bg-info",
  },
  {
    id: "completed",
    name: "Completed",
    controlClassName: "data-[state=checked]:border-success data-[state=checked]:bg-success",
  },
  {
    id: "paymentDue",
    name: "Payment Due",
    controlClassName: "data-[state=checked]:border-warning data-[state=checked]:bg-warning",
  },
  {
    id: "paymentError",
    name: "Payment Error",
    controlClassName: "data-[state=checked]:border-error data-[state=checked]:bg-error",
  },
  {
    id: "cancelled",
    name: "Cancelled",
    controlClassName: "data-[state=checked]:border-medium-gray data-[state=checked]:bg-medium-gray",
  },
] as const satisfies React.ComponentProps<typeof Filters>["gigStatus"]["options"];
export type GigStatusFilterOptions = (typeof gigStatusOptions)[number];
export const gigStatusOptionIds = gigStatusOptions.map(({ id }) => id);
export const defaultStatusIds = gigStatusOptionIds.filter((status) => status !== "cancelled");

const gigTypeOptions = [
  {
    id: "marketplace",
    name: "Marketplace",
  },
  {
    id: "network",
    name: "Network",
    icon: Globe,
  },
] as const satisfies NonNullable<React.ComponentProps<typeof Filters>["gigType"]>["options"];
export type GigTypeFilterOptions = (typeof gigTypeOptions)[number];
const taskTypeOption = { id: "task", name: "Task" } as const satisfies NonNullable<
  React.ComponentProps<typeof Filters>["gigType"]
>["options"][number];
/** The Gig Type options the Schedule offers: Task joins them only with `qwick-tasks` on. */
export const gigTypeOptionsFor = (includeTasks: boolean | undefined) =>
  includeTasks ? [...gigTypeOptions, taskTypeOption] : gigTypeOptions;
const gigTypeOptionIds = gigTypeOptions.map(({ id }) => id);
/** What an unfiltered Gig Type reads as, per `gigTypeOptionsFor`. */
export const gigTypeDefaultIds = (includeTasks: boolean | undefined) =>
  gigTypeOptionsFor(includeTasks).map(({ id }) => id);
/** Every id the `types` search param accepts, tasks included, so a link survives the flag flipping. */
export const gigTypeSearchIds = [...gigTypeOptionIds, taskTypeOption.id] as const;

export const eventsOptions = [
  { id: "gigs", name: "Shifts" },
  { id: "events", name: "Events", icon: Event },
] as const satisfies NonNullable<React.ComponentProps<typeof Filters>["events"]>["options"];
export const eventsOptionIds = eventsOptions.map(({ id }) => id);

export const interviewsOptions = [
  { id: "working", name: "Working", icon: AssignmentTurnedIn },
  { id: "phone", name: "Phone", icon: Phone },
  { id: "video", name: "Video", icon: Video },
  { id: "in_person", name: "In-person", icon: Pin },
] as const satisfies NonNullable<React.ComponentProps<typeof Filters>["interviews"]>["options"];
export const interviewsOptionIds = interviewsOptions.map(({ id }) => id);

export const adminFilterKeys = [
  "managed",
  "auto_select_enabled",
  "require_certifications",
  "location_name",
  "market_name",
  "skill_id",
  "pro_first_name",
  "pro_last_name",
  "employment_type",
  "type",
  "start_time__gte",
  "start_time__lte",
  "end_time__gte",
  "end_time__lte",
] as const satisfies Array<keyof MarketplaceSearch>;
